import { all, call, put, takeLatest, select } from '@redux-saga/core/effects';
import { toast } from 'react-toastify';
import { getNotifMsg, handleErrors } from '../../utils';
import { setCurrentUser, signupStart } from '../user/user.actions';
import {
  fetchWebsitesStart,
  fetchWebsitesStartSuccess,
  fetchWebsitesStartFailure,
  setWebsites,
  SetWebsiteStatus,
  loadingWebsites,
  loadingWhatsappBarcode,
  setWhatsappBarcode,
  setPermissionWhatsapp,
  setChannelsTeam,
  setAgentAssignByUrl,
  deleteWebsiteSuccess,
  deleteWebsiteFailure,
  setSelectedWorkspace,
} from './websites.actions';
import {
  createProfileObject,
  createWebsitesObject,
  createChannelsTeamObject,
} from './websites.adapter';
import {
  validateWebsite,
  handleAddNewWebsite,
  handleFetchWebsites,
  handleToggleWebsiteStatus,
  handleAssignOperators,
  handleFetchWhatsappBarcode,
  handleLogoutWhatsapp,
  handleSendContainersTeam,
  handleSendGroupMessage,
  handleSendWhatsappGroupMessage,
  handleDeleteWebsite,
} from './websites.helpers';
import websiteTypes from './websites.types';

const mapState = ({ appData }) => ({
  locale: appData.locale,
});

export function* addNewContainer({ payload }) {
  const formData = {
    otpToken: payload.otpToken,
    email: payload.email,
    site: payload.url,
    signUp: true,
    name: payload.title,
    phone: payload.mobile,
    referralCode: payload.referralCode,
  };

  const { locale } = yield select(mapState);

  try {
    const isValidatedUrl = yield validateWebsite(payload.url);
    payload.setDuplicateWebsite(!isValidatedUrl);
    if (!isValidatedUrl) return;

    // const websiteData = yield handleAddNewContainer(payload);
    // if (!websiteData.success) {
    //   toast.error(
    //     'در هنگام ايجاد سايت خطايی رخ داده است. لطفا دوباره تلاش كنيد.',
    //     {
    //       position: 'bottom-center',
    //     }
    //   );
    //   return;
    // }
    // const channelData = yield handleAddChannel({
    //   url,
    //   containerId: websiteData.res.id,
    //   title,
    // });
    yield put(signupStart(formData));
  } catch (error) {
    handleErrors(error, locale);
  }
}

export function* onAddNewContainerStart() {
  yield takeLatest(websiteTypes.ADD_NEW_CONTAINER_START, addNewContainer);
}

export function* fetchWebsites() {
  const { locale } = yield select(mapState);
  yield put(loadingWebsites());

  // channelId of the workspace saved in localstorage
  const wsSavedInlocalStorage = localStorage.getItem('SELECTED_WORKSPACE');

  try {
    const responseData = yield handleFetchWebsites();

    // adapted websites object
    const websitesObject = createWebsitesObject(responseData);
    const wsFoundByChannelIdSavedInLocalstorage =
      wsSavedInlocalStorage &&
      websitesObject.find((item) => item.id === wsSavedInlocalStorage);

    // if no selected workspace is found from localstorage then choose the first one as the selected workspace by default
    const selectedWorkspace =
      wsFoundByChannelIdSavedInLocalstorage || websitesObject[0];

    yield put(setWebsites(websitesObject));
    yield put(setSelectedWorkspace(selectedWorkspace));
    yield put(setCurrentUser(createProfileObject(responseData)));
    yield put(setChannelsTeam(createChannelsTeamObject(responseData.channels)));
    yield put(setAgentAssignByUrl(responseData.raypackage.agentAssignByUrl));
    yield put(fetchWebsitesStartSuccess(responseData));
    if (responseData.container.permissionWhatsapp) {
      yield put(setPermissionWhatsapp(true));
    }
  } catch (error) {
    handleErrors(error, locale);
    yield put(fetchWebsitesStartFailure(error));
  }
}

export function* fetchBarcode({ payload }) {
  const { locale } = yield select(mapState);
  yield put(loadingWhatsappBarcode());
  const { websiteId } = payload;
  try {
    const response = yield handleFetchWhatsappBarcode(websiteId);
    const barcode = response.data.code;

    yield put(setWhatsappBarcode({ barcode }));
  } catch (error) {
    const code = error.response?.status;
    const errorMsg = error.response?.data?.error;

    // already registered this channelId in whatsapp
    if (code === 409 && errorMsg === 'Already Loggedin') {
      yield put(setWhatsappBarcode({ alreadyLoggedIn: true }));
      return;
    }
    handleErrors(error, locale);
  }
}

export function* logoutFormWhatsapp({ payload }) {
  const { websiteId, setLoading } = payload;
  const { locale } = yield select(mapState);
  try {
    const response = yield handleLogoutWhatsapp(websiteId);

    if (response.success) {
      yield put(setWhatsappBarcode({ barcode: null, alreadyLoggedIn: false }));
    }
  } catch (error) {
    handleErrors(error, locale);
  } finally {
    setLoading(false);
  }
}

export function* onFetchWebsitesStart() {
  yield takeLatest(websiteTypes.FETCH_WEBSITES_START, fetchWebsites);
}

export function* onFetchWhatsappBarcodeStart() {
  yield takeLatest(websiteTypes.FETCH_WHATSAPP_BARCODE_START, fetchBarcode);
}

export function* onLogoutWhatsappStart() {
  yield takeLatest(websiteTypes.LOGOUT_WHATSAPP, logoutFormWhatsapp);
}

export function* addNewWebsite({ payload }) {
  const { url, handleToggle } = payload;
  const { locale } = yield select(mapState);

  try {
    const isValidatedUrl = yield validateWebsite(url);
    if (!isValidatedUrl) {
      toast.info(getNotifMsg('SITE_REGISTERED_BEFORE', locale));
      return;
    }
    const website = yield handleAddNewWebsite(payload);

    if (website.success) {
      yield put(fetchWebsitesStart());
      toast.success(getNotifMsg('WEBSITE_ADDED_SUCCESS', locale), {
        position: 'bottom-left',
      });
      handleToggle();
    }
  } catch (error) {
    const errorStatus = error.response?.status;
    const errorMsg =
      error.response.data?.data?.message || error.response.data?.message;

    if (errorStatus === 403 && errorMsg === 'Permission Denied') {
      toast.info(
        getNotifMsg('SITE_ALREADY_REGISTERED_IN_ANOTHER_ACCOUNT', locale)
      );
      return;
    }

    if (errorStatus === 403 && errorMsg === 'upgrade package') {
      toast.error(getNotifMsg('UPGRADE_TO_ADD_NEW_WEBSITE', locale));
      return;
    }

    if (errorMsg && errorMsg === 'can not crate new site') {
      toast.error(getNotifMsg('WEBSITE_NUM_MAX_ERROR', locale), {
        position: 'bottom-left',
      });
      return;
    }

    if (errorStatus === 403) {
      toast.error(getNotifMsg('SITE_REGISTERED_BEFORE', locale));
      return;
    }

    handleErrors(error, locale);
  }
}

export function* onAddNewWebsiteStart() {
  yield takeLatest(websiteTypes.ADD_NEW_WEBSITE_START, addNewWebsite);
}

export function* toggleWebsiteStatus({ payload }) {
  const { locale } = yield select(mapState);

  try {
    const isToggledStatus = yield handleToggleWebsiteStatus(payload);
    if (isToggledStatus) {
      yield put(SetWebsiteStatus(payload));
      payload.isActive
        ? toast.success(getNotifMsg('WEBSITE_ENABLE', locale))
        : toast.success(getNotifMsg('WEBSITE_DISABLED', locale));
    }
  } catch (error) {
    handleErrors(error, locale);
  }
}

export function* onToggleWebsiteStatusStart() {
  yield takeLatest(
    websiteTypes.TOGGLE_WEBSITE_STATUS_START,
    toggleWebsiteStatus
  );
}

export function* assignOperators({ payload }) {
  const { selectedOperatorIds, websiteId, toggle } = payload;
  const { locale } = yield select(mapState);

  try {
    const returnedData = yield handleAssignOperators(
      selectedOperatorIds,
      websiteId
    );
    if (!returnedData.success) {
      toast.error(getNotifMsg('PROBLEM_ASSIGNING_OPERATOR', locale));
      return;
    }
    toast.success(getNotifMsg('SELECTED_OPERATORS_ASSIGNED', locale));
    toggle();
    yield put(fetchWebsitesStart());
  } catch (error) {
    handleErrors(error, locale);
  }
}

export function* onAssignOperatorsStart() {
  yield takeLatest(websiteTypes.ASSIGN_OPERATORS_START, assignOperators);
}

export function* sendTeam({ payload }) {
  const { locale } = yield select(mapState);

  try {
    const returnedData = yield handleSendContainersTeam(payload);
    if (!returnedData.success) {
      toast.error(getNotifMsg('PROBLEM_IN_PROCESS', locale));
      return;
    }
    toast.success(getNotifMsg('CHANGES_SAVED', locale));
  } catch (error) {
    handleErrors(error, locale);
  }
}

export function* onSendContainersTeamStart() {
  yield takeLatest(websiteTypes.SEND_CONTAINERS_TEAM, sendTeam);
}

export function* sendGroupMessage({ payload }) {
  const { formData, loadingSubmitHandle } = payload;
  const { locale } = yield select(mapState);

  try {
    loadingSubmitHandle(true);
    const returnedData = yield handleSendGroupMessage(formData);
    if (!returnedData.success) {
      toast.error(getNotifMsg('PROBLEM_IN_PROCESS', locale));
      return;
    }
    toast.success(getNotifMsg('MESSAGE_SENT', locale));
  } catch (error) {
    if (error.response?.status === 422) {
      toast.error(getNotifMsg('MAX_GROUP_MSG_LIMIT_REACHED', locale));
      return;
    }
    handleErrors(error, locale);
  } finally {
    loadingSubmitHandle(false);
  }
}

export function* onSendGroupMessage() {
  yield takeLatest(websiteTypes.SEND_GROUP_MESSAGE, sendGroupMessage);
}

export function* sendWhatsappGroupMessage({ payload }) {
  const { formData, loadingSubmitHandle } = payload;
  const { locale } = yield select(mapState);

  try {
    loadingSubmitHandle(true);
    const formToSend = {
      type: 'contact',
      channelId: formData.channelId,
      message: formData.message,
    };

    const returnedData = yield handleSendWhatsappGroupMessage(formToSend);
    if (!returnedData.success) {
      toast.error(getNotifMsg('PROBLEM_IN_PROCESS', locale));
      return;
    }
    toast.success(getNotifMsg('MESSAGE_SENT', locale));
  } catch (error) {
    if (error.response?.status === 409) {
      toast.error(getNotifMsg('MAX_GROUP_MSG_LIMIT_REACHED', locale));
      return;
    }
    if (error.response?.status === 404) {
      toast.error(getNotifMsg('WEBSITE_NOT_CONNECTED_WHATSAPP', locale));
      return;
    }
    handleErrors(error, locale);
  } finally {
    loadingSubmitHandle(false);
  }
}

export function* onSendWhatsappGroupMessage() {
  yield takeLatest(
    websiteTypes.SEND_WHATSAPP_GROUP_MESSAGE,
    sendWhatsappGroupMessage
  );
}

export function* deleteWebsiteSaga({ payload }) {
  const { locale } = yield select(mapState);

  try {
    const response = yield handleDeleteWebsite(payload);
    yield put(deleteWebsiteSuccess(response));
    yield put({ type: websiteTypes.FETCH_WEBSITES_START });

    if (response.success) {
      toast.success(getNotifMsg('DELETE_WEBSITE', locale), {
        position: 'bottom-center',
      });
      return;
    }
  } catch (error) {
    yield put(deleteWebsiteFailure(error));
    handleErrors(error);
  }
}

export function* onDeletingWebsite() {
  yield takeLatest(websiteTypes.DELETE_WEBSITE, deleteWebsiteSaga);
}

function selectedWorkspace({ payload: selectedWebsite }) {
  localStorage.setItem('SELECTED_WORKSPACE', selectedWebsite.id);
}

function* onSetSelectedWorkspace() {
  yield takeLatest(websiteTypes.SET_SELECTED_WORKSPACE, selectedWorkspace);
}

export default function* websitesSagas() {
  yield all([
    call(onAddNewContainerStart),
    call(onFetchWebsitesStart),
    call(onAddNewWebsiteStart),
    call(onToggleWebsiteStatusStart),
    call(onAssignOperatorsStart),
    call(onFetchWhatsappBarcodeStart),
    call(onLogoutWhatsappStart),
    call(onSendContainersTeamStart),
    call(onSendGroupMessage),
    call(onSendWhatsappGroupMessage),
    call(onSetSelectedWorkspace),
    call(onDeletingWebsite),
  ]);
}
